import React, { useState } from 'react';
import BaseballDataDisplay from './BaseballDataDisplay';
import SoccerDataDisplay from './SoccerDataDisplay';
import BasketballDataDisplay from './BasketballDataDisplay';
import VolleyballDataDisplay from './VolleyballDataDisplay';
import HockeyDataDisplay from './HockeyDataDisplay';
import { dataDisplayStyles as styles } from './DataDisplay';

interface MatchListPanelProps {
  matchData: any;
  currentMatch: any;
}

const MatchListPanel: React.FC<MatchListPanelProps> = ({ matchData, currentMatch }) => {
  const [selectedTeam, setSelectedTeam] = useState<'home' | 'away' | null>('home');
  const [showGameIframe, setShowGameIframe] = useState(false);
  const [iframeUrl] = useState('');

  const isBaseball = matchData?.sport === 'baseball';
  const isSoccer = matchData?.sport === 'soccer';
  const isBasketball = matchData?.sport === 'basketball';
  const isVolleyball = matchData?.sport === 'volleyball';
  const isHockey = matchData?.sport === 'hockey';

  if (!matchData) {
    return <div style={styles.noData}>경기 데이터를 불러올 수 없음.</div>;
  }

  if (isBaseball) {
    return (
      <BaseballDataDisplay
        data={matchData}
        homeTeam={matchData?.home_team || ''}
        awayTeam={matchData?.away_team || ''}
      />
    );
  }

  if (isSoccer) {
    return (
      <SoccerDataDisplay
        data={matchData}
        homeTeam={matchData?.home_team || ''}
        awayTeam={matchData?.away_team || ''}
      />
    );
  }

  if (isBasketball) {
    return (
      <BasketballDataDisplay
        data={matchData}
        homeTeam={matchData?.home_team || ''}
        awayTeam={matchData?.away_team || ''}
      />
    );
  }

  if (isVolleyball) {
    return (
      <VolleyballDataDisplay
        data={matchData}
        homeTeam={matchData?.home_team || ''}
        awayTeam={matchData?.away_team || ''}
      />
    );
  }

  if (isHockey) {
    return (
      <HockeyDataDisplay
        data={matchData}
        homeTeam={matchData?.home_team || ''}
        awayTeam={matchData?.away_team || ''}
      />
    );
  }

  // Generic sport fallback
  return (
    <>
      {/* Team Selection Buttons - Inside matches tab */}
      <div style={styles.teamButtons}>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'home' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('home')}
        >
          홈팀: {matchData?.home_team || '알 수 없음'}
        </button>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'away' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('away')}
        >
          원정팀: {matchData?.away_team || '알 수 없음'}
        </button>
      </div>

      {/* Game detail iframe */}
      {showGameIframe && (
        <div style={styles.gameIframeContainer}>
          <div style={styles.iframeHeader}>
            <h3>경기 상세 페이지</h3>
            <button
              style={styles.modalCloseButton}
              onClick={() => setShowGameIframe(false)}
            >
              ✕
            </button>
          </div>
          <iframe
            src={iframeUrl}
            style={styles.gameIframe}
            title="Game Detail"
          />
        </div>
      )}

      {/* Team statistics shown when team is selected */}
      {selectedTeam && matchData && (
        <div style={styles.teamStatsSection}>
          <h3>
            {selectedTeam === 'home' ? matchData?.home_team : matchData?.away_team} 상세 지표
          </h3>

          {/* Season Statistics */}
          {matchData[`${selectedTeam}_team_stats`]?.season && Object.keys(matchData[`${selectedTeam}_team_stats`]?.season || {}).length > 0 && (
            <div style={styles.statsBlock}>
              <h4>시즌 전적</h4>
              <div style={{overflowX: 'auto'}}>
              <table style={styles.statsTable}>
                <thead>
                  <tr>
                    {Object.keys(matchData[`${selectedTeam}_team_stats`]?.season || {}).map((key) => (
                      <th key={key} style={styles.statsHeader}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {Object.values(matchData[`${selectedTeam}_team_stats`]?.season || {}).map((value, idx) => (
                      <td key={idx} style={styles.statsCell}>{String(value)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Recent Matches */}
          {matchData[`${selectedTeam}_team_stats`]?.recent_matches &&
           (matchData[`${selectedTeam}_team_stats`]?.recent_matches?.length || 0) > 0 && (
            <div style={styles.statsBlock}>
              <h4>최근 10경기</h4>
              {(() => {
                const matches = matchData[`${selectedTeam}_team_stats`]?.recent_matches || [];
                if (matches.length === 0) return null;

                // Get all unique keys from all matches
                const headers = Array.from(new Set(
                  matches.flatMap((match: any) => Object.keys(match))
                )).slice(0, 10) as string[];

                return (
                  <div style={{overflowX: 'auto'}}>
                  <table style={styles.dataTable}>
                    <thead>
                      <tr>
                        {headers.map((header: string) => (
                          <th key={header} style={styles.tableHeader}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {matches.slice(0, 10).map((match: any, idx: number) => (
                        <tr key={idx}>
                          {headers.map((header: string) => (
                            <td key={header} style={styles.tableCell}>
                              {match[header] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Head to Head Statistics */}
          {matchData?.head_to_head && (
            <div style={styles.statsBlock}>
              <h4>상대전적</h4>
              {matchData.head_to_head?.summary && (
                <div style={{overflowX: 'auto'}}>
                <table style={styles.summaryTable}>
                  <thead>
                    <tr>
                      <th style={styles.tableHeader}>전체 경기</th>
                      <th style={styles.tableHeader}>홈 승</th>
                      <th style={styles.tableHeader}>무</th>
                      <th style={styles.tableHeader}>원정 승</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={styles.tableCell}>{matchData.head_to_head.summary?.total_games || 0}</td>
                      <td style={styles.tableCell}>{matchData.head_to_head.summary?.home_wins || 0}</td>
                      <td style={styles.tableCell}>{matchData.head_to_head.summary?.draws || 0}</td>
                      <td style={styles.tableCell}>{matchData.head_to_head.summary?.away_wins || 0}</td>
                    </tr>
                  </tbody>
                </table>
                </div>
              )}
              {matchData.head_to_head?.recent_matches &&
               matchData.head_to_head.recent_matches.length > 0 && (
                <>
                  <h5 style={{marginTop: '15px', marginBottom: '10px'}}>최근 맞대결</h5>
                  {(() => {
                    const matches = matchData.head_to_head.recent_matches || [];
                    if (matches.length === 0) return null;

                    const headers = Array.from(new Set(
                      matches.flatMap((match: any) => Object.keys(match))
                    )).slice(0, 10) as string[];

                    return (
                      <div style={{overflowX: 'auto'}}>
                      <table style={styles.dataTable}>
                        <thead>
                          <tr>
                            {headers.map((header: string) => (
                              <th key={header} style={styles.tableHeader}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {matches.slice(0, 5).map((match: any, idx: number) => (
                            <tr key={idx}>
                              {headers.map((header: string) => (
                                <td key={header} style={styles.tableCell}>
                                  {match[header] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Show recent form if selected team */}
      {selectedTeam && matchData && (() => {
        const recentForm = selectedTeam === 'home'
          ? matchData?.home_recent_form
          : matchData?.away_recent_form;

        if (!recentForm || recentForm.length === 0) return null;

        const headers = Array.from(new Set(
          recentForm.flatMap((form: any) => Object.keys(form))
        )).slice(0, 10) as string[];

        return (
          <div style={styles.recentForm}>
            <h4>{selectedTeam === 'home' ? matchData?.home_team : matchData?.away_team} 최근 전적</h4>
            <div style={{overflowX: 'auto'}}>
            <table style={styles.dataTable}>
              <thead>
                <tr>
                  {headers.map((header: string) => (
                    <th key={header} style={styles.tableHeader}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentForm.map((form: any, idx: number) => (
                  <tr key={idx}>
                    {headers.map((header: string) => (
                      <td key={header} style={styles.tableCell}>
                        {form[header] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default MatchListPanel;
